import React, { useEffect, useState } from "react";
import { Category, Option } from "../../types/product-models";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AccessType, RoleProtectedRoute } from "./RoleProtectedRoute";

// Pages
import { LandingPage } from "../pages/LandingPage";
import { SignupPage } from "../pages/SignupPage";
import { EmailSignupPage } from "../pages/EmailSignupPage";
import { SocialSignupPage } from "../pages/SocialSignupPage";
import { SocialSignupCompletePage } from "../pages/SocialSignupCompletePage";
import { CompanySignupPage } from "../pages/CompanySignupPage";
import { CompanyAdminPage } from "../pages/CompanyAdmin/CompanyAdminPage";
import { NaverCallbackPage } from "../pages/NaverCallbackPage";
import { TermsOfServicePage } from "../pages/TermsOfServicePage";
import { PrivacyPolicyPage } from "../pages/PrivacyPolicyPage";
import EditProfile1 from "../pages/EditProfile1";
import UpdateProfile2 from "../pages/EditProfile2";
import { AuthSuccessPage } from "../pages/AuthSuccessPage";
import { AuthFailedPage } from "../pages/AuthFailedPage";
import CompanyInfo from "../pages/CompanyAdmin/CompanyInfo";
import CompanyMembers from "../pages/CompanyAdmin/CompanyMembers";
import BusinessClientManagement from "../pages/CompanyAdmin/BusinessClientManagement";
import BusinessClientDetailPage from "../pages/CompanyAdmin/BusinessClientDetail";
import ManagedClientDetail from "../pages/CompanyAdmin/ManagedClientDetail";
import FormLayout from "../pages/CompanyApplication";
import { CompanyRequests } from "../pages/Admin/CompanyRequests";
import WithdrawalRequests from "../pages/Admin/WithdrawalRequests";
import ManageRegisteredCompanies1 from "../pages/Admin/ManageRegisteredCompanies1";
import ManageRegisteredCompanies2 from "../pages/Admin/ManageRegisteredCompanies2";
import Modal from "../components/UI/Modal";
import { Button } from "../components/UI/Button";
import GlobalMessageModal from "../components/UI/GlobalMessageModal";
import { CompanyProtectedRoute } from "../routes/CompanyProtectedRoute";
import { MallDesignCheck } from "../pages/CompanyAdmin/MallDesignCheck";
import Equipments from "../pages/CompanyAdmin/Equipments";
import ProductManagementPage from "../pages/CompanyAdmin/ProductManagementPage";
import ProductSystemDetailPage from "../pages/CompanyAdmin/ProductSystemDetailPage";
import { ProductManagementWrapper } from "../components/CompanyAdmin/ProductManagementWrapper";
import { KpiCardDashboard } from "../pages/CompanyAdmin/KpiCardDashboard";
import OrderManagement from "../pages/CompanyAdmin/OrderManagement/index";
import ResubmissionManagement from "../pages/CompanyAdmin/ResubmissionManagement";
import ShippingManagement from "../pages/CompanyAdmin/ShippingManagement";
import ShopHome from "../pages/Shop/ShopHome";
import ShopSignup from "../pages/Shop/ShopSignup";
import ProductPage from "../pages/Shop/ProductPage";
import MaterialManagementPage from "presentation/pages/CompanyAdmin/MaterialManagementPage";
import InventoryManagementPage from "presentation/pages/CompanyAdmin/InventoryManagementPage";
import TodoTemplateManagement from "../pages/CompanyAdmin/TodoTemplateManagement";
import TodoStatusManagement from "../pages/CompanyAdmin/TodoStatusManagement";
import ProductionRecordManagement from "../pages/CompanyAdmin/ProductionRecordManagement";
import MaintenanceManagement from "../pages/CompanyAdmin/MaintenanceManagement";
import NoticeManagement from "../pages/CompanyAdmin/NoticeManagement";
import FaqManagement from "../pages/CompanyAdmin/FaqManagement";
import OrganizationManagement from "../pages/CompanyAdmin/OrganizationManagement";
import InvitationAcceptPage from "../pages/InvitationAcceptPage";
import Users from "../pages/Admin/Users";
import UnreportedMachineProducts from "../pages/Admin/UnreportedMachineProducts";
import MachineProductManagement from "../pages/Admin/MachineProductManagement";
import MachineProductForm from "../pages/Admin/MachineProductForm";
import InstalledMachineManagement from "../pages/Admin/InstalledMachineManagement";
import EmailSignature from "../pages/Admin/EmailSignature";
import BankTransferRevenue from "../pages/Admin/BankTransferRevenue";
import { BankTransfer } from "../pages/CompanyAdmin/BankTransfer";
import {
  BankTransferBillingFail,
  BankTransferBillingSuccess,
} from "../pages/CompanyAdmin/BankTransferBillingCallback";
import { TossPayments } from "../pages/CompanyAdmin/TossPayments";
import { NaverMap } from "../pages/NaverMap";
import FontManagement from "../pages/CompanyAdmin/FontManagement";
import TaxReceipt from "../pages/CompanyAdmin/TaxReceipt";
import ShippingFee from "../pages/CompanyAdmin/ShippingFee";
import PopupManagement from "../pages/CompanyAdmin/PopupManagement";
import PortfolioManagement from "../pages/CompanyAdmin/PortfolioManagement";
import PortfolioCreatePage from "../pages/CompanyAdmin/PortfolioManagement/PortfolioCreatePage";
import RefundManagement from "../pages/CompanyAdmin/RefundManagement";
import NotificationManagement from "../pages/CompanyAdmin/NotificationManagement";
import AlarmCenter from "../pages/CompanyAdmin/AlarmCenter";
import WasteRateManagementPage from "../pages/CompanyAdmin/WasteRateManagementPage";
import ShippingFeeManagementPage from "../pages/CompanyAdmin/ShippingFeeManagementPage";
import EventDiscountManagementPage from "../pages/CompanyAdmin/EventDiscountManagementPage";
import QuoteInquiryManagement from "../pages/CompanyAdmin/QuoteInquiryManagement";
import OrderRevisionRequestManagement from "../pages/CompanyAdmin/OrderRevisionRequestManagement";
import EmailSettings from "../pages/CompanyAdmin/EmailSettings";
import Unregister from "../pages/Unregister";
import NewEquipmentPage from "../pages/CompanyAdmin/NewEquipmentPage";
import NewRoutersPage from "../pages/CompanyAdmin/NewRoutersPage";
import StatusManagement from "../pages/CompanyAdmin/StatusManagement";
import NewRouterManagement from "../pages/CompanyAdmin/NewRouterManagement";
import MailInbox from "../pages/CompanyAdmin/MailInbox";
import ForgotPasswordPage from "../pages/ForgotPasswordPage";
import ResetPasswordPage from "../pages/ResetPasswordPage";
import EmailVerificationRequiredPage from "../pages/EmailVerificationRequiredPage";
import EmailVerifiedPage from "../pages/EmailVerifiedPage";

// Query Client 설정
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5분
    },
    mutations: {
      retry: 1,
    },
  },
});

export const AppRouter: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <GlobalMessageModal />
        <Routes>
          <Route path="map" element={<NaverMap />} />
          <Route path="/shop">
            <Route index element={<ShopHome />} />
            <Route path="signup" element={<ShopSignup />} />
            <Route path=":productId" element={<ProductPage />} />
          </Route>

          {/* 인증 라우트 */}
          <Route path="auth">
            <Route path="callback" element={<AuthSuccessPage />} />
            <Route path="failed" element={<AuthFailedPage />} />
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
            <Route path="reset-password" element={<ResetPasswordPage />} />
            <Route path="invitation" element={<InvitationAcceptPage />} />
            <Route path="email-verification" element={<EmailVerificationRequiredPage />} />
            <Route path="email-verified" element={<EmailVerifiedPage />} />
            <Route path="naver">
              <Route path="callback" element={<NaverCallbackPage />} />
            </Route>
          </Route>

          {/* 최상단 라우트 */}
          <Route path="/" element={<LandingPage />} />
          <Route path="terms" element={<TermsOfServicePage />} />
          <Route path="privacy" element={<PrivacyPolicyPage />} />

          {/* 회원가입 라우트 */}
          <Route path="signup">
            <Route index element={<SignupPage />} />

            <Route path="email" element={<EmailSignupPage />} />
            <Route
              path="social"
              element={
                <RoleProtectedRoute>
                  <SocialSignupPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="complete"
              element={
                <RoleProtectedRoute>
                  <SocialSignupCompletePage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="company"
              element={
                <RoleProtectedRoute>
                  <CompanySignupPage />
                </RoleProtectedRoute>
              }
            />
          </Route>

          {/* 회사 라우트 */}
          <Route path="company">
            <Route
              path="bank-transfer/billing/success"
              element={<BankTransferBillingSuccess />}
            />
            <Route
              path="bank-transfer/billing/fail"
              element={<BankTransferBillingFail />}
            />
            <Route
              path="apply"
              element={
                <RoleProtectedRoute accessType={AccessType.DEFAULT}>
                  <FormLayout />
                </RoleProtectedRoute>
              }
            />

            <Route
              path=":companyId"
              element={
                <CompanyProtectedRoute>
                  <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                    <Outlet />
                  </RoleProtectedRoute>
                </CompanyProtectedRoute>
              }
            >
              <Route
                index
                element={
                  <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                    <CompanyAdminPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="info"
                element={
                  <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                    <CompanyInfo />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="mall-design"
                element={
                  <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                    <MallDesignCheck />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="members"
                element={
                  <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                    <CompanyMembers />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="organization"
                element={
                  <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                    <OrganizationManagement />
                  </RoleProtectedRoute>
                }
              />
              {/* 예전 구성원 관리·부서 관리 주소는 조직 관리로 합쳤다 */}
              <Route path="staff" element={<Navigate to="../organization" replace />} />
              <Route path="departments" element={<Navigate to="../organization" replace />} />
              <Route
                path="business-clients"
                element={
                  <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                    <BusinessClientManagement />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="business-clients/:userId"
                element={
                  <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                    <BusinessClientDetailPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="business-clients/managed/:clientId"
                element={
                  <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                    <ManagedClientDetail />
                  </RoleProtectedRoute>
                }
              />

              <Route
                path="equipments"
                element={
                  <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                    <NewEquipmentPage />
                  </RoleProtectedRoute>
                }
              />

              <Route
                path="new-equipments"
                element={
                  <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                    <NewEquipmentPage />
                  </RoleProtectedRoute>
                }
              />

              <Route
                path="new-routers"
                element={
                  <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                    <NewRoutersPage />
                  </RoleProtectedRoute>
                }
              />

              <Route
                path="routers"
                element={
                  <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                    <NewRouterManagement />
                  </RoleProtectedRoute>
                }
              />

              {/* 우측 상단 알림 아이콘이 여는 알림함. 이메일·문자 발송 관리(orders/notifications)와는 다르다 */}
              <Route path="alarms" element={<AlarmCenter />} />

              <Route path="payment">
                <Route path="bank-transfer" element={<BankTransfer />} />
                <Route path="toss-payments" element={<TossPayments />} />
              </Route>

              <Route path="orders">
                <Route index element={<OrderManagement />} />
                <Route
                  path="resubmission"
                  element={<ResubmissionManagement />}
                />
                {/* 무통장입금 확인은 결제 관리 > 무통장 입금으로 합쳤다 */}
                <Route
                  path="deposit-check"
                  element={<Navigate to="../../payment/bank-transfer" replace />}
                />
                <Route path="shipping" element={<ShippingManagement />} />
                <Route path="shipping-fee" element={<ShippingFee />} />
                <Route path="tax-receipt" element={<TaxReceipt />} />
                <Route path="refund" element={<RefundManagement />} />
                <Route
                  path="notifications"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <NotificationManagement />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="quote-inquiries"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <QuoteInquiryManagement />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="order-revision-requests"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <OrderRevisionRequestManagement />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="status-management"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <StatusManagement />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="mail-inbox"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <MailInbox />
                    </RoleProtectedRoute>
                  }
                />
              </Route>

              {/* 이메일 설정 (회사 관리 섹션) */}
              <Route
                path="email-settings"
                element={
                  <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                    <EmailSettings />
                  </RoleProtectedRoute>
                }
              />

              {/* 상품 관리 라우트 */}
              <Route element={<ProductManagementWrapper />}>
                <Route
                  path="products"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <ProductManagementPage />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="products/new"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <ProductSystemDetailPage />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="products/:productId"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <ProductSystemDetailPage />
                    </RoleProtectedRoute>
                  }
                />
              </Route>

              {/* 회사 KPI — admin 정부보고와 독립된 단일 페이지(벤치마크 + 그래프) */}
              <Route path="kpi" element={<KpiCardDashboard />} />

              <Route path="material" element={<MaterialManagementPage />} />
              <Route path="inventory" element={<InventoryManagementPage />} />

              {/* 가격 정보 관리 라우트 */}
              <Route path="pricing">
                <Route
                  path="waste-rate"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <WasteRateManagementPage />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="shipping-fee"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <ShippingFeeManagementPage />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="event-discount"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <EventDiscountManagementPage />
                    </RoleProtectedRoute>
                  }
                />
              </Route>

              {/* 작업 관리 라우트 */}
              <Route path="tasks">
                <Route
                  path="templates"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <TodoTemplateManagement />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="status"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <TodoStatusManagement />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="production-records"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <ProductionRecordManagement />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="maintenance"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <MaintenanceManagement />
                    </RoleProtectedRoute>
                  }
                />
              </Route>

              {/* 컨텐츠 관리 라우트 */}
              <Route path="contents">
                <Route
                  path="notices"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <NoticeManagement />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="faqs"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <FaqManagement />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="fonts"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <FontManagement />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="popups"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <PopupManagement />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="portfolios"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <PortfolioManagement />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="portfolios/new"
                  element={
                    <RoleProtectedRoute accessType={AccessType.COMPANY_ADMIN}>
                      <PortfolioCreatePage />
                    </RoleProtectedRoute>
                  }
                />
              </Route>
            </Route>
          </Route>

          <Route path="admin">
            <Route
              index
              element={<Navigate to="/admin/company/requests" replace />}
            />
            <Route path="company">
              <Route
                path="requests"
                element={
                  <RoleProtectedRoute accessType={AccessType.ADMIN}>
                    <CompanyRequests />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="withdrawal-requests"
                element={
                  <RoleProtectedRoute accessType={AccessType.ADMIN}>
                    <WithdrawalRequests />
                  </RoleProtectedRoute>
                }
              />
              <Route path="manage">
                <Route
                  index
                  element={
                    <RoleProtectedRoute accessType={AccessType.ADMIN}>
                      <ManageRegisteredCompanies1 />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path=":companyId"
                  element={
                    <RoleProtectedRoute accessType={AccessType.ADMIN}>
                      <ManageRegisteredCompanies2 />
                    </RoleProtectedRoute>
                  }
                />
              </Route>
            </Route>
            <Route
              path="users"
              element={
                <RoleProtectedRoute accessType={AccessType.ADMIN}>
                  <Users />
                </RoleProtectedRoute>
              }
            />
            {/* 무통장입금 자동확인 매출 — 회사를 가로지르는 숫자라 회사 상세 밖에 둔다 */}
            <Route
              path="bank-transfer/revenue"
              element={
                <RoleProtectedRoute accessType={AccessType.ADMIN}>
                  <BankTransferRevenue />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="email-signature"
              element={
                <RoleProtectedRoute accessType={AccessType.ADMIN}>
                  <EmailSignature />
                </RoleProtectedRoute>
              }
            />
            <Route path="machine-product">
              <Route
                index
                element={
                  <RoleProtectedRoute accessType={AccessType.ADMIN}>
                    <MachineProductManagement />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="unreported"
                element={
                  <RoleProtectedRoute accessType={AccessType.ADMIN}>
                    <UnreportedMachineProducts />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="new"
                element={
                  <RoleProtectedRoute accessType={AccessType.ADMIN}>
                    <MachineProductForm />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path=":id/edit"
                element={
                  <RoleProtectedRoute accessType={AccessType.ADMIN}>
                    <MachineProductForm />
                  </RoleProtectedRoute>
                }
              />
            </Route>
            <Route
              path="machine"
              element={
                <RoleProtectedRoute accessType={AccessType.ADMIN}>
                  <InstalledMachineManagement />
                </RoleProtectedRoute>
              }
            />
          </Route>

          <Route path="profile">
            <Route
              index
              element={
                <RoleProtectedRoute accessType={AccessType.DEFAULT}>
                  <EditProfile1 />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="update"
              element={
                <RoleProtectedRoute accessType={AccessType.DEFAULT}>
                  <UpdateProfile2 />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="unregister"
              element={
                <RoleProtectedRoute accessType={AccessType.DEFAULT}>
                  <Unregister />
                </RoleProtectedRoute>
              }
            />
          </Route>

          {/* 폴백 라우트 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};
